import { Component, Input, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { AccountService } from 'app/account/account.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';

@Component({
  selector: 'app-flyout-holdout-settings',
  templateUrl: './flyout-holdout-settings.component.html',
  styleUrls: ['./flyout-holdout-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutHoldoutSettingsComponent {
  private readonly flyoutService = inject(FlyoutService);
  private readonly accountService = inject(AccountService);
  private readonly logger: Logger = inject(LoggerFactory).get('FlyoutHoldoutSettingsComponent');

  protected enabled = signal<boolean>(false);
  protected percentage = signal<number>(10);
  protected saving = signal<boolean>(false);
  protected errorKey = signal<string | null>(null);

  @Input() public set flyoutOpeningData(data: FlyoutOpeningData) {
    if (data) {
      this.enabled.set(!!data.holdoutEnabled);
      this.percentage.set(data.holdoutPercentage ?? 10);
    }
  }

  protected toggleEnabled(checked: boolean): void {
    this.enabled.set(checked);
  }

  protected onPercentageInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.percentage.set(+input.value);
  }

  private isPercentageValid(): boolean {
    const value = this.percentage();
    return Number.isFinite(value) && value >= 1 && value <= 50;
  }

  protected closeFlyout(): void {
    this.flyoutService.close();
  }

  protected async save(): Promise<void> {
    if (this.enabled() && !this.isPercentageValid()) {
      this.errorKey.set('generic.validation.error-wrong-number');
      return;
    }
    this.errorKey.set(null);
    this.saving.set(true);
    try {
      const value = this.enabled() ? { percentage: this.percentage() } : null;
      await this.accountService
        .patchCurrentAccountSettingsProperty([{ propertyName: 'holdoutGroupSettings', value }])
        .toPromise();
      this.flyoutService.close();
    } catch (e) {
      this.logger.error('Failed to save holdout settings: ' + e);
      this.errorKey.set('generic.error');
    } finally {
      this.saving.set(false);
    }
  }
}
